import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

interface PillButtonProps {
    /** Renders a `Link`. Omit it to render a real `button` instead. */
    href?: string
    type?: 'button' | 'submit'
    disabled?: boolean
    onClick?: () => void
    children: ReactNode
    tone?: 'brand' | 'cream' | 'ink'
    className?: string
}

/**
 * The site's primary call to action: a rounded pill with a circular fill that
 * scales up from the centre on hover, matching the original's button motion.
 *
 * Without an `href` it renders a `button`, so forms can submit through the same
 * component rather than reimplementing the motion.
 */
export const PillButton: FC<PillButtonProps> = ({
    href,
    type = 'button',
    disabled,
    onClick,
    children,
    tone = 'brand',
    className
}): ReactNode => {
    const tones = {
        brand: 'bg-brand text-cream',
        cream: 'bg-cream text-brand',
        ink: 'bg-ink text-cream'
    }

    const fills = {
        brand: 'bg-ink',
        cream: 'bg-brand',
        ink: 'bg-brand'
    }

    const hoverText = {
        brand: 'group-hover:text-cream',
        cream: 'group-hover:text-cream',
        ink: 'group-hover:text-cream'
    }

    const shell = cn(
        'group relative inline-flex items-center justify-center overflow-hidden rounded-full px-7 py-4 text-[16px] font-medium leading-none tracking-[-0.01em] transition-colors duration-300 md:text-[18px]',
        tones[tone],
        disabled && 'pointer-events-none opacity-60',
        className
    )

    const inner = (
        <>
            <span
                aria-hidden='true'
                className={cn(
                    'absolute left-1/2 top-1/2 aspect-square w-[130%] -translate-x-1/2 -translate-y-1/2 scale-0 rounded-full transition-transform duration-500 ease-out group-hover:scale-100',
                    fills[tone]
                )}
            />

            <span className={cn('relative z-10 whitespace-nowrap transition-colors duration-300', hoverText[tone])}>
                {children}
            </span>
        </>
    )

    if (!href) {
        return (
            <button
                type={type}
                onClick={onClick}
                disabled={disabled}
                className={shell}
            >
                {inner}
            </button>
        )
    }

    return (
        <Link href={href} className={shell}>
            {inner}
        </Link>
    )
}
